import { ApiError, OFFLINE, type Api } from "../api/client.js";
import type { BundleView, PayeeAddStep, PayeeView, Receipt } from "../api/types.js";
import { BankCard } from "../ui/bankcard.js";
import { loadFailed } from "../ui/busy.js";
import type { CheckSignView } from "../ui/check-sign.js";
import { ADD_COPY, addedLine, addRefusal, type PayeeAddRequest } from "./add-payee-model.js";
import { afterLostConfirm } from "./card-end.js";
import { isExpired, type CardSigner, type Prepared, type SignResult } from "./card-signer.js";
import { refusalMessage } from "./stop.js";
import { failureOf } from "./surfaces.js";

/**
 * DCTA-124 — 17 at `add` ends on Check and sign (UX spec §F.2 row 13, F7; ADR-0013).
 *
 *  1. The bank builds the card from the screen's request and nothing else (`POST /v1/payees/additions`:
 *     the number and the customer's own name for them — never a payee id, a name or a wait).
 *  2. The card is drawn from `GET /v1/bundles/{id}` and nothing else: shown = signed (13b–13d).
 *  3. Sign → the passkey (CardSigner: the ceremony is the tap's first asynchronous act, one at a time).
 *  4. Signed and executed → the bank's payee, read fresh, on its page (13f). Signed and not executed →
 *     what did not happen (13h–13j). A refusal at any step is the bank's card, never a form error (§E.7).
 *
 * Every card that is left — its back, Not now, a newer card, a card nobody waited for — is cancelled at
 * the bank, so nothing is left that could be signed. A slow bank gets 10 s (§E.7), then the
 * couldn't-reach state, and what it builds after that is cancelled.
 */
export type AddFlowApi = Pick<Api, "addPayee" | "bundle" | "cancelBundle" | "payee" | "receipt">;
export type SignerLike = Pick<CardSigner, "prepare" | "sign" | "signing">;

export interface AddFlowDeps {
  readonly api: AddFlowApi;
  readonly view: CheckSignView;
  /** The signer for one card (CardSigner in the app; a fake in a test). */
  readonly signer: (bundleId: string) => SignerLike;
  /** Talk to us — the bank's number and hours, on the screen's sheet. */
  readonly onTalk: () => void;
  /** Not now: the add screen is left. */
  readonly onNotNow: () => void;
  /** The bank added them: its payee as read fresh (undefined if the read failed), 13f's toast, and the payee as signed. */
  readonly onAdded: (payee: PayeeView | undefined, done: string, signed: PayeeAddStep["payee"]) => void;
  readonly onSignedOut: () => void;
  /** DCTA-199 ruling (Claude, 13 Oct): a card a passkey signs speaks "Check and sign." — the drawn card, to be read aloud. */
  readonly onCard?: ((bundleId: string) => void) | undefined;
  /** No passkeys on this phone: the passkey sheet's "not available here". */
  readonly onUnsupported: () => void;
}

/** §E.7: Check and sign shows its skeleton for up to 10 s, then the error state. */
export const BUILD_MS = 10_000;

const LATE = "the customer stopped waiting before it was shown";

/**
 * Did the bank decide (its sentence, for its notice) — or could it not be reached? The app's one rule
 * (`failureOf`): a transport failure, a busy bank or one that failed has no sentence; DCTA-126's 503
 * SCREENING_UNAVAILABLE does (§F.2 13i — the name check is down, the bank says so).
 */
const bankSaid = (error: unknown): error is ApiError => error instanceof ApiError && failureOf(error).kind === "bank";

interface Live {
  readonly card: BankCard;
  readonly bundle: BundleView;
  readonly signer: SignerLike;
  prepared: Prepared | undefined;
  expiry: ReturnType<typeof setTimeout> | undefined;
}

export class AddFlow {
  readonly #d: AddFlowDeps;
  #request: PayeeAddRequest | undefined;
  /** The current build; an answer to an older one is late. */
  #seq = 0;
  #live: Live | undefined;
  #timer: ReturnType<typeof setTimeout> | undefined;

  constructor(deps: AddFlowDeps) {
    this.#d = deps;
  }

  /** A passkey sheet is up: Back must not take the screen away. */
  get busy(): boolean {
    return this.#live?.signer.signing === true;
  }

  /** Check and sign: the screen is pushed and the bank asked for the card. */
  start(request: PayeeAddRequest): void {
    this.#request = request;
    this.#d.view.push();
    this.#build();
  }

  /** The screen's own back (and the system's): the card is cancelled at the bank; back to the name. */
  back(): void {
    if (this.busy) return;
    this.#leave("the customer went back from Check and sign");
    this.#pop();
  }

  /** Everything stops (the add screen closed under it): the card is cancelled at the bank. */
  close(): void {
    this.#leave("the customer left the add screen");
    this.#pop();
  }

  // ----------------------------------------------------------------- build
  #build(): void {
    const request = this.#request;
    if (request === undefined) return;
    // A card still waiting for a signature (not sent, or live) is cancelled before a newer one is made;
    // `#leave` also makes any build still in flight late.
    this.#leave("superseded by a newer card");
    const seq = this.#seq;
    // Every answer to an older build — or to this one after its 10 s — is late: what it made is cancelled.
    const stale = (): boolean => seq !== this.#seq;
    this.#building();
    this.#timer = setTimeout(() => {
      this.#seq += 1;
      this.#timer = undefined;
      this.#unreachable();
    }, BUILD_MS);
    void this.#d.api.addPayee(request).then(
      async (made) => {
        if (stale()) {
          void this.#cancel(made.bundle_id, LATE);
          return;
        }
        let bundle: BundleView;
        try {
          bundle = await this.#d.api.bundle(made.bundle_id);
        } catch (error) {
          void this.#cancel(made.bundle_id, LATE);
          if (stale()) return;
          this.#stopTimer();
          this.#failed(error);
          return;
        }
        if (stale()) {
          void this.#cancel(made.bundle_id, LATE);
          return;
        }
        this.#stopTimer();
        this.#present(bundle);
      },
      (error: unknown) => {
        if (stale()) return;
        this.#stopTimer();
        this.#failed(error);
      },
    );
  }

  #stopTimer(): void {
    if (this.#timer !== undefined) clearTimeout(this.#timer);
    this.#timer = undefined;
  }

  #present(bundle: BundleView): void {
    const signer = this.#d.signer(bundle.bundle_id);
    let live: Live | undefined;
    const card = new BankCard(bundle, {
      onSign: () => {
        if (live !== undefined) this.#sign(live);
      },
      // "Not now" (13e): nothing is signed, the card is cancelled at the bank, the add screen is left.
      onEdit: () => {
        if (live === undefined || this.busy) return;
        this.#leave("the customer chose not now on Check and sign");
        this.#d.onNotNow();
      },
      // Expired → Check again; Not sent → Try again: a fresh card from the same request.
      onReask: () => this.#build(),
      onRetry: () => this.#build(),
    });
    live = { card, bundle, signer, prepared: undefined, expiry: undefined };
    this.#live = live;
    this.#d.view.show(card.element, card.element.getAttribute("aria-label") ?? "");
    this.#d.onCard?.(bundle.bundle_id);
    // The payload lives 120 s (180 s in large type) — measured from now, ended one second early (as in the chat).
    const ttl = Date.parse(bundle.signing_payload.expires_at) - Date.parse(bundle.signing_payload.created_at);
    if (Number.isFinite(ttl) && ttl > 0) {
      const ending = live;
      live.expiry = setTimeout(() => {
        if (this.#live === ending) ending.card.expire();
      }, Math.max(0, ttl - 1000));
    }
    void this.#prepare(live);
  }

  /** Before the tap: the ceremony's options; the Sign button holds (busy, label kept) until they land. */
  async #prepare(live: Live): Promise<void> {
    live.card.busy(true);
    try {
      live.prepared = await live.signer.prepare();
      if (live.prepared === "first") live.card.line(ADD_COPY.firstSign);
    } catch (error) {
      if (this.#live !== live) return;
      if (isExpired(error)) live.card.expire();
      else {
        // Without the options the button would ignore a tap: the card goes, and the bank's state is said.
        this.#leave(LATE);
        this.#failed(error);
      }
      return;
    } finally {
      if (this.#live === live) live.card.busy(false);
    }
  }

  // ------------------------------------------------------------------ sign
  /** The Sign tap: its first asynchronous act is the ceremony (CardSigner). */
  #sign(live: Live): void {
    if (this.#live !== live || !live.card.signable) return;
    if (live.prepared === "unsupported") {
      this.#d.onUnsupported();
      return;
    }
    const ceremony = live.signer.sign(() => live.card.line(ADD_COPY.saved));
    if (ceremony === undefined) return;
    live.card.failure(null);
    live.card.busy(true);
    void ceremony.then((r) => this.#signed(live, r));
  }

  async #signed(live: Live, r: SignResult): Promise<void> {
    if (this.#live !== live) return;
    live.card.busy(false);
    switch (r.kind) {
      case "signed":
        await this.#done(live, r.receipt);
        return;
      case "cancelled":
      case "timed-out":
      case "create-cancelled":
        live.card.failure(r.kind);
        return;
      case "saved":
        live.card.line(ADD_COPY.saved);
        return;
      case "unsupported":
        this.#d.onUnsupported();
        return;
      case "expired":
        live.card.expire();
        return;
      case "failed":
        await this.#signFailed(live, r.error);
        return;
    }
  }

  /** What the signature did: the payee added (13f), or what did not happen (13h–13j). */
  async #done(live: Live, receipt: Receipt): Promise<void> {
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    live.expiry = undefined;
    const step = live.bundle.signing_payload.steps.find((s): s is PayeeAddStep => s.type === "payee_add");
    const ran = receipt.steps.find((s) => s.step_id === step?.step_id || s.index === step?.index);
    if (step === undefined || ran?.state !== "executed") {
      // DCTA-126 (§F.2 13h–13j): the receipt says why, in the bank's words — printed as sent; with no
      // sentence, only what the state itself says. Its Talk to us (13j) is a ghost, as on every refusal.
      const support = (ran?.next_actions ?? []).find((a) => a.action.kind === "support");
      live.card.phase({
        status: "Not added",
        label: "Bank: add a payee, not added",
        leaves: false,
        notices: [{ icon: "i-alert", cls: "end", lead: ran?.customer_message ?? "Nothing was added." }],
        acts: support === undefined ? [] : [{ label: support.label, kind: "ghost", onSelect: () => this.#d.onTalk() }],
      });
      return;
    }
    live.card.signed(receipt);
    this.#live = undefined;
    const payee = await this.#d.api.payee(step.payee.payee_id).catch(() => undefined);
    this.#d.onAdded(payee, addedLine(step.payee.nickname ?? step.payee.registered_name), step.payee);
  }

  /**
   * The signature went out and no answer came back: whether the payee was added is the bank's record,
   * never a guess (as the chat's lost confirm). Still waiting → Not sent; ran → as signed; anything
   * else → the card stays as it was, and nothing is claimed.
   */
  async #signFailed(live: Live, error: unknown): Promise<void> {
    if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
      this.#d.onSignedOut();
      return;
    }
    if (error instanceof ApiError && (error.reasonCode === OFFLINE || error.status >= 500)) {
      live.card.busy(true);
      const view = await this.#d.api.bundle(live.bundle.bundle_id).catch(() => undefined);
      if (this.#live !== live) return;
      const next = view === undefined ? "unknown" : afterLostConfirm(view.state, view.expired);
      if (next === "recover") {
        const receipt = await this.#d.api.receipt(live.bundle.bundle_id).catch(() => undefined);
        if (receipt !== undefined) {
          live.card.busy(false);
          await this.#done(live, receipt);
          return;
        }
      }
      live.card.busy(false);
      if (next === "not-sent") live.card.notSent();
      else if (next === "expired") live.card.expire();
      else if (next === "cancelled") live.card.cancelled();
      else this.#d.view.said(["Can't reach the bank right now."], []);
      return;
    }
    // The gateway refused the signature (its own sentence, §F.2 11r): said as it said it; a fresh card is one tap away.
    const lead = error instanceof ApiError ? (error.serverMessage ?? "Nothing was added.") : "Nothing was added.";
    this.#leave(LATE);
    this.#d.view.said([lead], [{ label: "Try again", ghost: false, onSelect: () => this.#build() }]);
  }

  // ------------------------------------------------------------------ ends
  /** The bank would not build the card, or could not be reached. */
  #failed(error: unknown): void {
    if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
      this.#d.onSignedOut();
      return;
    }
    if (!bankSaid(error)) {
      this.#unreachable();
      return;
    }
    // A bank decision: its sentence and next step, as the bank said them (§E.7: never a form error that
    // pretends to be the bank) — DCTA-124: not a Bank card (that is only what is signed), no rule id;
    // Talk to us as a ghost when the bank offers it.
    const m = refusalMessage(addRefusal(error), (a) => (a.kind === "support" ? this.#d.onTalk() : undefined));
    this.#d.view.said(m.lines, m.acts.map((a) => ({ label: a.label, ghost: true, onSelect: a.onSelect })));
  }

  #unreachable(): void {
    this.#d.view.show(loadFailed(ADD_COPY.noCard.lead, ADD_COPY.noCard.rest, () => this.#build()), `${ADD_COPY.noCard.lead}. ${ADD_COPY.noCard.rest}`);
  }

  /**
   * The live card goes: its timer stops, and — if it can still be signed — it is cancelled at the bank. A
   * build still in flight becomes late (what it makes is cancelled when it lands, never drawn).
   */
  #leave(reason: string): void {
    this.#seq += 1;
    this.#stopTimer();
    const live = this.#live;
    this.#live = undefined;
    if (live === undefined) return;
    if (live.expiry !== undefined) clearTimeout(live.expiry);
    if (live.card.life === "live" || live.card.life === "not-sent") void this.#cancel(live.bundle.bundle_id, reason);
  }

  #building(): void {
    this.#d.view.building();
  }

  #pop(): void {
    this.#d.view.pop();
  }

  #cancel(bundleId: string, reason: string): Promise<unknown> {
    return this.#d.api.cancelBundle(bundleId, reason).catch(() => undefined);
  }
}

